import { createContext, useContext, useMemo, type ReactNode } from "react";
import { hasPermission, canAccessRoute, type Permission } from "@folio/shared/permissions";

interface TenantMembership {
  tenantId: string;
  tenantName: string;
  roles: string[];
  permissions: string[];
  isDefault: boolean;
}

interface PermissionsContextValue {
  permissions: string[];
  currentTenant: TenantMembership | null;
  tenants: TenantMembership[];
  hasPermission: (permission: Permission) => boolean;
  canAccessRoute: (route: string) => boolean;
  isLoading: boolean;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

interface PermissionsProviderProps {
  children: ReactNode;
  tenants: TenantMembership[];
  currentTenantId: string | null;
  isLoading?: boolean;
}

/**
 * Provides permission checking capabilities to the app.
 *
 * Permissions are fetched from the /auth/me endpoint and include
 * all permissions for the user's roles in the current tenant.
 */
export function PermissionsProvider({
  children,
  tenants,
  currentTenantId,
  isLoading = false,
}: PermissionsProviderProps) {
  const value = useMemo<PermissionsContextValue>(() => {
    const currentTenant = currentTenantId
      ? tenants.find((t) => t.tenantId === currentTenantId) ?? null
      : tenants.find((t) => t.isDefault) ?? tenants[0] ?? null;

    const permissions = currentTenant?.permissions ?? [];

    return {
      permissions,
      currentTenant,
      tenants,
      hasPermission: (permission: Permission) =>
        hasPermission(permissions, permission),
      canAccessRoute: (route: string) => canAccessRoute(permissions, route),
      isLoading,
    };
  }, [tenants, currentTenantId, isLoading]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * Hook to access permission checking functions.
 *
 * @example
 * const { hasPermission, canAccessRoute } = usePermissions();
 *
 * // Check specific permission
 * if (hasPermission("contabilidad:diario:write")) {
 *   // Show edit button
 * }
 *
 * // Check route access
 * if (canAccessRoute("/contabilidad/diario")) {
 *   // Show nav item
 * }
 */
export function usePermissions(): PermissionsContextValue {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
}

/**
 * Component that only renders children if user has the required permission.
 */
export function RequirePermission({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { hasPermission: checkPermission } = usePermissions();
  return checkPermission(permission) ? <>{children}</> : <>{fallback}</>;
}
